/**
 * Funnel Event Logger
 * Tracks user progression through the funnel and sends logs to WordPress backend
 */

import { getDeviceInfo } from './deviceDetection'
import { getExperimentId } from './experimentId'
import { getEverflowTransactionIdsByOffer } from './everflow'
import { logger } from '../utils/logger'
import { captureException } from './sentry'
import { findTestByMaskedId, findTestByVariantName } from '../../../functions/ab-testing/config'
import { sendBrazeCampaign } from './sendBrazeCampaign'

/**
 * Get experiment name from experiment ID
 * @param {string} experimentId - Experiment ID (can be masked ID or variant name)
 * @returns {string|null} Experiment name or null
 */
const getExperimentName = (experimentId) => {
  if (!experimentId) return null
  
  // Try to find by masked ID first
  const byMaskedId = findTestByMaskedId(experimentId)
  if (byMaskedId) {
    return byMaskedId.testName
  }
  
  // Try to find by variant name
  const byVariantName = findTestByVariantName(experimentId)
  if (byVariantName) {
    return byVariantName.testName
  }
  
  return null
}

/**
 * Default Everflow offer_id per funnel_id. Mirrors the assignment in
 * `FunnelContext.jsx` (and the routing logic in `everflow.js`) so we can
 * look up the funnel-specific `site_ef_transaction_id_offer_<id>` cookie
 * at log-build time when only `funnel_id` is known.
 *
 * Keep this in sync with:
 *   - `FunnelContext.jsx`'s offer_id resolution (CV-1 and CV-2 → 1)
 *   - `everflow.js`'s `getEverflowLandingConfig()` (`/nad → 3`, default → 1)
 *
 * Note: The `oid=23` URL override is not represented here because it is a
 * runtime URL parameter rather than a fixed funnel mapping. Callers can
 * still drive that case explicitly via `everflow_offer_id` on `eventData`.
 */
const FUNNEL_ID_TO_DEFAULT_OFFER_ID = {
  'variant-cv-1': '1',
  'variant-cv-2': '1',
  'variant-cv-3': '1',
  'variant-cv-4': '1',
  'variant-cv-nad': '3',
  // TODO: Replace placeholder offer_id 4 with the real Sermorelin Everflow offer ID
  'variant-cv-sermorelin': '4'
}

/**
 * Resolve the Everflow offer_id for the funnel being logged.
 * Priority:
 *   1. Caller-supplied `everflow_offer_id` on `eventData` (most accurate;
 *      reflects the funnel-specific value persisted in funnelData).
 *   2. `funnel_id` → default offer_id mapping above.
 *
 * @param {Object} eventData - Log event data
 * @returns {string|null} Offer id as a string, or null if it cannot be resolved.
 */
const resolveOfferIdForLog = (eventData) => {
  if (eventData?.everflow_offer_id) return String(eventData.everflow_offer_id)
  const fromFunnelId = eventData?.funnel_id && FUNNEL_ID_TO_DEFAULT_OFFER_ID[eventData.funnel_id]
  return fromFunnelId || null
}

const SUCCESSFUL_PAYMENT_STATUSES = new Set(['succeeded', 'success', 'paid', 'completed'])
const successfulPaymentStatusByKey = new Map()

// Opening /checkout/selection/:caseId or /checkout/payment/:caseId restores a
// case. That load must not write a funnel-user-actions-log row. Logging
// starts again on the first click or keypress.
const CASE_RESTORE_URL = /\/checkout\/(?:selection|payment)\/[a-f0-9-]{36}(?:\/|$)/i
let suppressLogsForCaseRestore = false

export const suppressFunnelLogsForCaseRestore = () => {
  if (typeof window === 'undefined' || suppressLogsForCaseRestore) return
  if (!CASE_RESTORE_URL.test(window.location.pathname || '')) return

  suppressLogsForCaseRestore = true
  const enableLogging = () => {
    suppressLogsForCaseRestore = false
    window.removeEventListener('pointerdown', enableLogging, true)
    window.removeEventListener('keydown', enableLogging, true)
  }
  window.addEventListener('pointerdown', enableLogging, true)
  window.addEventListener('keydown', enableLogging, true)
}

const paymentStatusMemoryKey = (entryId, caseId) =>
  `${String(entryId || '').trim()}::${String(caseId || '').trim()}`

export const isSuccessfulPaymentStatus = (status) =>
  SUCCESSFUL_PAYMENT_STATUSES.has(String(status || '').trim().toLowerCase())

// goToStep and the checkout guard can log a stale `failed` after Stripe has
// already returned succeeded. Keep the successful status for this entry.
const resolvePaymentStatusForLog = (entryId, caseId, paymentStatus) => {
  const incoming = String(paymentStatus || '').trim()
  const incomingNormalized = incoming.toLowerCase()
  const keys = [paymentStatusMemoryKey(entryId, caseId)]
  if (caseId) keys.push(paymentStatusMemoryKey(entryId, ''))

  if (isSuccessfulPaymentStatus(incoming)) {
    keys.forEach((key) => successfulPaymentStatusByKey.set(key, incoming))
    return incoming
  }

  const remembered = keys.map((key) => successfulPaymentStatusByKey.get(key)).find(Boolean)
  if (remembered && incomingNormalized === 'failed') {
    logger.debug('💳 Ignoring failed payment_status log after a successful payment:', remembered)
    return remembered
  }

  return incoming || null
}

/**
 * Build the complete log payload with all required fields
 * @param {Object} params - Log parameters
 * @returns {Object} Complete log payload
 */
const buildLogPayload = (eventData = {}) => {
  const {
    entry_id,
    email,
    current_step_key_name,
    current_step_index,
    highest_step_reached_key_name,
    highest_step_reached_index,
    payment_status,
    upsell_status,
    current_upsell_step,
    funnel_id,
    experiment_id,
    experiment_name,
    affid,
    ol_approval_status,
    cv_approval_status,
    case_id,
    ef_tid,
    everflow_offer_id,
    everflow_sub1,
    everflow_sub2,
    everflow_sub3,
    everflow_sub4,
    everflow_sub5,
    everflow_coupon_code,
    promo_code,
    promo_id,
    promo_percent_off,
    promo_amount_off
  } = eventData

  const deviceInfo = getDeviceInfo()
  const now = new Date().toISOString()

  // Per-offer Everflow transaction IDs read from
  // `site_ef_transaction_id_offer_<id>` cookies. Each funnel writes its
  // own cookie, so when a user has visited /cv-1 (offer_id 1) and /nad
  // (offer_id 3) in the same browser this records both transaction IDs
  // alongside the current funnel's `ef_tid`. Caller-supplied
  // `ef_tid_offer_<id>` keys on `eventData` win over the cookie value
  // (useful for tests / explicit overrides).
  const efTidsByOfferFromCookies = getEverflowTransactionIdsByOffer()
  const efTidsByOfferFromEvent = Object.fromEntries(
    Object.entries(eventData).filter(
      ([key, value]) => key.startsWith('ef_tid_offer_') && value
    )
  )
  const efTidsByOffer = { ...efTidsByOfferFromCookies, ...efTidsByOfferFromEvent }

  // Resolve the `ef_tid` to the funnel actually being logged. The
  // authoritative source is the per-funnel cookie
  // `site_ef_transaction_id_offer_<offer_id>`; the caller-supplied
  // `ef_tid` (typically `funnelData.ef_tid`) is used only as a fallback
  // when the cookie hasn't been written yet (e.g. EF.click() still
  // pending) so the field never silently goes empty.
  const offerIdForLog = resolveOfferIdForLog(eventData)
  const efTidFromCookie = offerIdForLog
    ? efTidsByOffer[`ef_tid_offer_${offerIdForLog}`]
    : null
  const resolvedEfTid = efTidFromCookie || ef_tid || null
  const includePaymentStatus = Object.prototype.hasOwnProperty.call(eventData, 'payment_status')

  return {
    entry_id: entry_id || '',
    email: email || null,
    current_step_key_name: current_step_key_name || '',
    current_step_index: current_step_index || 0,
    highest_step_reached_key_name: highest_step_reached_key_name || '',
    highest_step_reached_index: highest_step_reached_index || 0,
    created_at: now,
    updated_at: now,
    ip: deviceInfo.ip, // Will be null on client-side, WordPress should populate from request
    os: deviceInfo.os,
    browser_agent: deviceInfo.userAgent,
    device_type: deviceInfo.deviceType,
    referrer_url: typeof window !== 'undefined' ? window.location.href : null,
    ...(includePaymentStatus
      ? { payment_status: resolvePaymentStatusForLog(entry_id, case_id, payment_status) }
      : {}),
    upsell_status: upsell_status || null,
    ...(current_upsell_step ? { current_upsell_step } : {}),
    funnel_id: funnel_id || null,
    experiment_id: experiment_id || null,
    experiment_name: experiment_name || getExperimentName(experiment_id),
    affid: affid || null,
    ol_approval_status: ol_approval_status || null,
    cv_approval_status: cv_approval_status || null,
    case_id: case_id || null,
    ef_tid: resolvedEfTid,
    ...efTidsByOffer,
    everflow_offer_id: everflow_offer_id || null,
    everflow_sub1: everflow_sub1 || null,
    everflow_sub2: everflow_sub2 || null,
    everflow_sub3: everflow_sub3 || null,
    everflow_sub4: everflow_sub4 || null,
    everflow_sub5: everflow_sub5 || null,
    everflow_coupon_code: everflow_coupon_code || null,
    promo_code: promo_code || null,
    promo_id: promo_id || null,
    promo_percent_off: promo_percent_off || null,
    promo_amount_off: promo_amount_off || null,
  }
}

/**
 * Send log event to WordPress backend
 * @param {Object} payload - Log data to send
 * @returns {Promise<Object>} Response from server
 */
const sendLogToBackend = async (payload) => {
  try {
    const response = await fetch('/funnel-user-actions-log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  } catch (error) {
    logger.error('❌ Error sending funnel log:', error)
    captureException(error, {
      tags: { module: 'funnelLogger', action: 'send_log' },
      extra: { payload: JSON.stringify(payload) },
    })
    throw error
  }
}

/**
 * Main logging function - logs funnel events
 * @param {Object} eventData - Event data to log
 * @param {string} eventData.entry_id - Unique entry ID
 * @param {string} eventData.email - User email (optional)
 * @param {string} eventData.current_step_key_name - Current step ID/name
 * @param {number} eventData.current_step_index - Current step index
 * @param {string} eventData.highest_step_reached_key_name - Highest step ID/name
 * @param {number} eventData.highest_step_reached_index - Highest step index
 * @param {string} eventData.payment_status - Payment status (optional)
 * @param {string} eventData.funnel_id - Funnel identifier (optional)
 * @param {string} eventData.experiment_id - A/B experiment variant ID, e.g. from ?_vt= (optional)
 * @param {string} eventData.experiment_name - Explicit experiment name override (optional)
 * @param {string} eventData.affid - Affiliate ID (optional)
 * @param {string} eventData.ol_approval_status - OL (OpenLoop) approval status: 'approved' or 'rejected' (optional)
 * @param {string} eventData.cv_approval_status - CV (CareValidate) approval status: 'approved' or 'rejected' (optional)
 * @param {string} eventData.case_id - CareValidate case ID (optional)
 * @param {string} eventData.ef_tid - Fallback Everflow transaction ID
 *   (optional). The logged `ef_tid` is auto-resolved from the funnel
 *   being logged: we look up `site_ef_transaction_id_offer_<offer_id>`
 *   where `offer_id` comes from `eventData.everflow_offer_id` or, if
 *   absent, from the `funnel_id → offer_id` map (`variant-cv-1 → 1`,
 *   `variant-cv-nad → 3`). The supplied `ef_tid` is used only when no
 *   matching cookie is present yet (e.g. EF.click() still pending).
 *   Per-funnel transaction IDs are also merged in automatically as
 *   `ef_tid_offer_<id>` keys (e.g. `ef_tid_offer_1` for /cv-1,
 *   `ef_tid_offer_3` for /nad). Callers may pass explicit
 *   `ef_tid_offer_<id>` overrides on `eventData`; those win over cookies.
 * @param {string} eventData.everflow_offer_id - Everflow offer ID
 *   (optional). When provided, drives the per-funnel `ef_tid` lookup.
 * @param {string} eventData.promo_code - Promo code applied (optional)
 * @param {string} eventData.promo_id - Promo ID (optional)
 * @param {string} eventData.promo_percent_off - Promo percent discount (optional)
 * @param {string} eventData.promo_amount_off - Promo amount discount (optional)
 * @returns {Promise<void>}
 */
export const logFunnelEvent = async (eventData) => {
  // Don't log if no entry_id
  if (!eventData.entry_id) {
    logger.warn('⚠️ Funnel logging skipped: No entry_id provided')
    return
  }

  if (suppressLogsForCaseRestore) {
    logger.debug('⏭️ Skipping funnel-user-actions-log while a case URL is loading')
    return
  }

  try {
    // Use URL/cookie experiment id when not provided (e.g. returning user / abandoned flow)
    const eventDataWithExperiment = {
      ...eventData,
      experiment_id: eventData.experiment_id ?? getExperimentId(),
    }
    logger.debug('eventDataWithExperiment:', eventDataWithExperiment)
    const payload = buildLogPayload(eventDataWithExperiment)
    
    const efTidByOfferSummary = Object.fromEntries(
      Object.entries(payload).filter(([key]) => key.startsWith('ef_tid_offer_'))
    )

    logger.debug('📊 Logging funnel event:', {
      entry_id: payload.entry_id,
      current_step: `${payload.current_step_key_name} (${payload.current_step_index})`,
      highest_step: `${payload.highest_step_reached_key_name} (${payload.highest_step_reached_index})`,
      payment_status: payload.payment_status,
      experiment_id: payload.experiment_id,
      device: `${payload.os} | ${payload.device_type}`,
      ef_tid: payload.ef_tid,
      ef_tid_by_offer: efTidByOfferSummary,
    })

    // Send to backend (fire-and-forget, don't block user flow)
    sendLogToBackend(payload).catch(err => {
      logger.error('❌ Funnel log failed (non-blocking):', err.message)
      captureException(err, {
        tags: { module: 'funnelLogger', action: 'log_event_failed' },
        extra: { 
          errorMessage: err.message,
          // Do not send eventData - may contain PHI (HIPAA compliance)
        },
        level: 'warning',
      })
    })
  } catch (error) {
    logger.error('❌ Error building funnel log:', error)
    captureException(error, {
      tags: { module: 'funnelLogger', action: 'build_log' },
      extra: { 
        errorMessage: error.message,
        // Do not send eventData - may contain PHI (HIPAA compliance)
      },
      level: 'warning',
    })
    // Don't throw - logging should never interrupt user flow
  }
}

/**
 * Get current step information from funnel config
 * @param {string} funnelId - Funnel identifier
 * @param {number} stepIndex - Step index
 * @param {Object} config - Funnel configuration
 * @returns {Object} Step information
 */
export const getCurrentStepInfo = (funnelId, stepIndex, config) => {
  if (!config || !config.steps || !Array.isArray(config.steps)) {
    return {
      key_name: 'unknown',
      index: stepIndex || 0,
    }
  }

  const step = config.steps[stepIndex]
  
  return {
    key_name: step?.id || 'unknown',
    index: stepIndex || 0,
  }
}

/**
 * Get highest step information
 * @param {Object} funnelData - Funnel context data
 * @param {Object} config - Funnel configuration
 * @returns {Object} Highest step information
 */
export const getHighestStepInfo = (funnelData, config) => {
  const highestIndex = funnelData.highest_step_reached_index || 0
  
  if (!config || !config.steps || !Array.isArray(config.steps)) {
    return {
      key_name: 'unknown',
      index: highestIndex,
    }
  }

  const step = config.steps[highestIndex]
  
  return {
    key_name: step?.id || 'unknown',
    index: highestIndex,
  }
}

/**
 * Log payment status update
 * @param {string} entry_id - Entry ID
 * @param {string} status - Payment status (pending, failed, successful)
 * @param {Object} additionalData - Additional data (email, step info, etc.)
 */
export const logPaymentStatus = (entry_id, status, additionalData = {}) => {
  if (!entry_id) {
    logger.warn('⚠️ Payment status logging skipped: No entry_id provided')
    return
  }

  logFunnelEvent({
    entry_id,
    payment_status: status,
    ...additionalData,
  })

  logger.debug(`💳 Payment status logged: ${status}`)
}

/**
 * Log checkout completed event
 * 
 * Called immediately after a successful payment and case submission.
 * Sends the full cart data (including the actual discounted price) to the backend
 * so Braze receives the correct purchase amount without needing a CareValidate API lookup.
 * 
 * @param {Object} funnelData - Current funnel data (from FunnelContext)
 * @param {string} caseId     - CareValidate case ID from successful submission
 * @returns {Promise<Object|undefined>} Server response or undefined on error
 */
export const logCheckoutCompleted = async (funnelData, caseId) => {
  // Post-purchase Braze campaign for /cv-1/, /nad/, /sermorelin/ (gated inside util).
  // Fired independently of checkout-completed logging so it still runs if entry_id is missing.
  if (funnelData && caseId) {
    sendBrazeCampaign({ ...funnelData, funnel_id: funnelData.funnel_id || '' }, caseId)
  }

  if (!funnelData?.entry_id || !funnelData?.email || !caseId) {
    logger.warn('⚠️ Checkout completed logging skipped: Missing entry_id, email, or caseId')
    return
  }

  try {
    const payload = {
      entry_id:                    funnelData.entry_id,
      funnel_id:                   funnelData.funnel_id || '',
      affid:                       funnelData.affid || '',
      email:                       funnelData.email,
      phone:                       funnelData.phone || '',
      first_name:                  funnelData.first_name || '',
      last_name:                   funnelData.last_name || '',
      gender:                      funnelData.gender || '',
      weight:                      funnelData.weight || '',
      goal_weight:                 funnelData.goal_weight || '',
      lose_weight:                 funnelData.lose_weight || '',
      bmi:                         funnelData.bmi || '',
      date_of_birth:               funnelData.date_of_birth || '',
      height_inches:               funnelData.height_inches || '',
      height_feet:                 funnelData.height_feet || '',
      product_select:              funnelData.product_select || '',
      selected_product_id:         funnelData.selected_product_id || '',
      selected_product_name:       funnelData.selected_product_name || '',
      selected_product_price:      funnelData.selected_product_price || '',
      selected_product_bundle_id:  funnelData.selected_product_bundle_id || '',
      selected_product_type:       funnelData.selected_product_type || '',
      selected_product_medication: funnelData.selected_product_medication || '',
      selected_plan:               funnelData.selected_plan || '',
      chosen_medicine_name:        funnelData.chosen_medicine_name || '',
      chosen_medicine_price:       funnelData.chosen_medicine_price || '',
      sms_subscription_checkbox:   funnelData.sms_subscription_checkbox ? 1 : 0,
      case_id:                     caseId,
      ef_tid:                      funnelData.ef_tid || null,
      everflow_offer_id:           funnelData.everflow_offer_id || null,
      everflow_coupon_code:        funnelData.everflow_coupon_code || null,
      promo_code:                  funnelData.promo_code || null,
      promo_id:                    funnelData.promo_id || null,
      promo_percent_off:           funnelData.promo_percent_off || null,
      promo_amount_off:            funnelData.promo_amount_off || null,
      unique_effects:              funnelData.unique_effects_options || null,
      how_you_sleep:               funnelData.how_you_sleep_options || null
    }

    logger.debug('[Checkout Completed] Sending payload for entry_id:', funnelData.entry_id, 'case_id:', caseId)

    const response = await fetch('/funnel-checkout-completed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const result = await response.json()
    logger.debug('[Checkout Completed] Logged successfully:', result)
    return result
  } catch (error) {
    logger.error('[Checkout Completed] Error logging checkout completed:', error)
    // Never throw — this must not interrupt the user's post-payment flow
  }
}

/**
 * Clear logging session (for testing purposes)
 */
export const clearLoggingSession = () => {
  logger.debug('🗑️ Logging session cleared')
}

export default {
  logFunnelEvent,
  getCurrentStepInfo,
  getHighestStepInfo,
  logPaymentStatus,
  logCheckoutCompleted,
  clearLoggingSession,
}

export { sendBrazeCampaign }
